import { Column } from '@tanstack/react-table';
import { DatePicker, DatePickerInput } from '@carbon/react';
import { debounce } from 'lodash-es';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { parseMomentUtc, SERVER_DATE_FORMAT } from 'utils/dates';
import { DataTableColumnMeta } from './types';
import styles from './header-date-filter.module.scss';

// ─────────────────────────────────────
// Props
// ─────────────────────────────────────

interface HeaderDateFilterProps<TData, TValue> {
  column: Column<TData, TValue>;
}

// ─────────────────────────────────────
// Component
// ─────────────────────────────────────

/**
 * Inline date range filter for the table header filter row.
 * Displays compact start and end date inputs, debounced before it updates the
 * column filter.
 *
 * Columns may opt into `meta.dateFilterLocalTime` (used by the eval3 master-data
 * date columns). When set, the filter renders a SINGLE Carbon `datePickerType="range"`
 * picker — the same shape as the working panel filter (`filter/date-filter.tsx`) —
 * instead of two separate `datePickerType="single"` pickers. One range instance owns
 * both bounds, so the End value repaints correctly (two single pickers left the End
 * input blank until a remount, e.g. pinning the column — the reported bug). It also
 * parses/formats in LOCAL time, symmetric round-trip.
 *
 * The flag is OFF by default, so every other table (this component is shared app-wide)
 * keeps its exact existing two-single-pickers behavior.
 * @param {HeaderDateFilterProps} props - Component props
 * @param {Column} props.column - TanStack Table column instance
 * @return {JSX.Element} The header date filter element
 */
export function HeaderDateFilter<TData, TValue>({
  column,
}: HeaderDateFilterProps<TData, TValue>) {
  const meta = column.columnDef.meta as DataTableColumnMeta | undefined;
  const scoped = meta?.dateFilterLocalTime === true;

  const rawFilterValue = column.getFilterValue() as
    [string | null, string | null] | undefined;
  const filterValue = useMemo<[string | null, string | null]>(
    () => rawFilterValue ?? [null, null],
    [rawFilterValue]
  );
  const [localValue, setLocalValue] = useState(filterValue);

  const debouncedSetFilter = useMemo(
    () =>
      debounce((value: [string | null, string | null]) => {
        const isEmpty = !value[0] && !value[1];
        column.setFilterValue(isEmpty ? undefined : value);
      }, 300),
    [column]
  );

  // Cancel debounce on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      debouncedSetFilter.cancel();
    };
  }, [debouncedSetFilter]);

  // Sync from external changes
  useEffect(() => {
    setLocalValue(filterValue);
  }, [filterValue]);

  // ── Scoped: single range picker (mirrors the working panel date filter) ──
  if (scoped) {
    // Range mode fires onChange with [start] after the first pick and [start, end]
    // after the second — a single flatpickr instance owns both, so the End value
    // renders reliably.
    const handleRangeChange = (dates: Date[]) => {
      const [start, end] = dates;
      const newValue: [string | null, string | null] = [
        start ? moment(start).format(SERVER_DATE_FORMAT) : null,
        end ? moment(end).format(SERVER_DATE_FORMAT) : null,
      ];
      setLocalValue(newValue);
      debouncedSetFilter(newValue);
    };

    // Local-time parse (symmetric with the local format above).
    const rangeValue: Date[] = [];
    if (localValue[0]) {
      rangeValue.push(moment(localValue[0], SERVER_DATE_FORMAT).toDate());
    }
    if (localValue[1]) {
      rangeValue.push(moment(localValue[1], SERVER_DATE_FORMAT).toDate());
    }

    return (
      <div className={`${styles.dateFilter} ${styles.rangeFilter}`}>
        <DatePicker
          datePickerType="range"
          dateFormat="m/d/Y"
          value={rangeValue}
          onChange={handleRangeChange}
        >
          <DatePickerInput
            id={`filter-${column.id}-start`}
            labelText=""
            placeholder="Start"
            size="sm"
          />
          <DatePickerInput
            id={`filter-${column.id}-end`}
            labelText=""
            placeholder="End"
            size="sm"
          />
        </DatePicker>
      </div>
    );
  }

  // ── Default (every other table): original two single pickers, unchanged ──
  const handleStartChange = (dates: Date[]) => {
    const val = dates[0] ? moment(dates[0]).format(SERVER_DATE_FORMAT) : null;
    const newValue: [string | null, string | null] = [val, localValue[1]];
    setLocalValue(newValue);
    debouncedSetFilter(newValue);
  };

  const handleEndChange = (dates: Date[]) => {
    const val = dates[0] ? moment(dates[0]).format(SERVER_DATE_FORMAT) : null;
    const newValue: [string | null, string | null] = [localValue[0], val];
    setLocalValue(newValue);
    debouncedSetFilter(newValue);
  };

  return (
    <div className={styles.dateFilter}>
      <DatePicker
        datePickerType="single"
        dateFormat="m/d/Y"
        value={
          localValue[0] ? parseMomentUtc(localValue[0]).toDate() : undefined
        }
        onChange={handleStartChange}
      >
        <DatePickerInput
          id={`filter-${column.id}-start`}
          labelText=""
          placeholder="Start"
          size="sm"
        />
      </DatePicker>
      <DatePicker
        datePickerType="single"
        dateFormat="m/d/Y"
        value={
          localValue[1] ? parseMomentUtc(localValue[1]).toDate() : undefined
        }
        onChange={handleEndChange}
      >
        <DatePickerInput
          id={`filter-${column.id}-end`}
          labelText=""
          placeholder="End"
          size="sm"
        />
      </DatePicker>
    </div>
  );
}
